import React, { Component } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import { ClientService, LocationService, ProjectService } from "@services";
import utils from "@utils";
import { GOOGLE_MAP_SETTINGS } from "@constants";
import { ConfirmationModal, LocationModal, ScrollDetect } from "../../../../components";

class LocationsTab extends Component {
	constructor(props) {
		super(props);

		this.state = {
			locations: [],
			locationsLoaded: false,
			projectId: "",
			addingLocation: false,
			editingLocation: undefined,
		};
	}

	componentDidMount = () => {
		this.getFirstProject();
	};

	/** Retrieves a list of locations for the client */
	getLocations = () => {
		const { lang } = global;
		const { clientId } = this.props;

		ClientService.getLocations(clientId)
			.then(({ data }) => {
				this.setState({
					locations: data,
					locationsLoaded: true,
				});
			})
			.catch(() => {
				this.setState({ locationsLoaded: true });
				utils.notify({
					type: "error",
					message: lang.t("Unable to load client locations"),
				});
			});
	};

	/** Retrieves the default project for the client since coordinators do not have access to separate projects */
	getFirstProject = () => {
		const { lang } = global;
		const { clientId } = this.props;

		ProjectService.getAll({
			clientId,
			includeAll: false,
			minimalData: true,
		})
			.then(({ data }) => {
				const projectId = data?.ItemList[0]?.Id ?? "";
				this.setState({ projectId });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Client Projects"),
				});
			});
	};

	/** Formats client contact objects into values that can be read by `ReactSelect` */
	parseClientContacts = () => {
		const { contacts } = this.props;

		return contacts.map(contact => ({
			value: contact.Id,
			label: contact.ContactName,
		}));
	};

	/**
	 * Prompts the user with a confirmation modal when they choose to delete a location.
	 * If a location has jobs associated with it, it cannot be deleted.
	 * @param {Object} location The location record to be deleted
	 */
	onDeleteLocation = location => () => {
		const { lang } = global;
		if (location.IsAssociatedWithJob) {
			utils.notify({
				type: "error",
				message: lang.t(
					"Unable to delete, since there are jobs associated with this location",
				),
			});
		} else {
			this._confirmationModal.openModal({
				title: lang.t("Delete Location"),
				message: lang.t("Are you sure you want to delete this location_"),
				confirmButton: `${lang.t("Yes")}, ${lang.t("Delete")}`,
				cancelButton: `${lang.t("No")}, ${lang.t("Keep this location_")}`,
				onConfirm: () => {
					LocationService.delete(location.Id)
						.then(() => {
							this.setState(prevState => ({
								locations: prevState.locations.filter(
									loc => loc.Id !== location.Id,
								),
							}));
						})
						.catch(() => {
							utils.notify({
								type: "error",
								message: lang.t("Error deleting location"),
							});
						});
				},
			});
		}
	};

	onHideLocationModal = () =>
		this.setState({ addingLocation: false, editingLocation: undefined });

	/**
	 * Builds a source URL for an image showing the location using Google Maps
	 * @param {Object} obj
	 * @param {Number} obj.Latitude The latitude coordinate for the client location
	 * @param {Number} obj.Longitude The longitude coordinate for the client location
	 */
	drawMapImage = ({ Latitude, Longitude }) => {
		const center = `${Latitude},${Longitude}`;
		const markers = `size:small|color:0x3099F0|${center}`;
		const queryParams = queryString.stringify({
			center,
			markers,
			zoom: 15,
			size: "75x75",
			key: GOOGLE_MAP_SETTINGS.key,
		});

		return (
			<img
				src={`${GOOGLE_MAP_SETTINGS.staticMapURL}?${queryParams}`}
				alt="locationMap"
			/>
		);
	};

	render() {
		const { lang } = global;
		const { locations, locationsLoaded, projectId, addingLocation, editingLocation } =
			this.state;
		const { user } = this.props;

		return (
			<div id="client-view-locations">
				<button
					type="button"
					aria-label={lang.t("Add Location")}
					className="btn btn-link client-view-add-btn"
					onClick={() => this.setState({ addingLocation: true })}
					title={lang.t("Add Location")}
				>
					<i className="rkf rkf-plus text-primary" />
				</button>

				<div
					className="col-xs-12"
					id="clients-view-locations-table"
				>
					<div
						id="clients-view-locations-table-header"
						className="row hidden-xs"
					>
						<div className="col-sm-2" />
						<div className="col-sm-4">
							<strong>{lang.t("Location Details")}</strong>
						</div>
						<div className="col-sm-4">
							<strong>{lang.t("Contact Details")}</strong>
						</div>
						<div className="col-sm-2" />
					</div>
					<div className="row">
						<div
							id="clients-view-locations-table-body"
							className="col-xs-12"
						>
							<For
								each="item"
								index="index"
								of={locations}
							>
								<div
									role="row"
									tabIndex={index}
									className="row"
									key={`client_view_location_item_${index}`}
								>
									<div className="col-sm-2 col-xs-4">
										{this.drawMapImage(item)}
									</div>

									<div className="col-sm-4 col-xs-8">
										<div>
											<strong>{item.Name}</strong>
										</div>
										<small>
											{`${item.StreetAddress1 ? `${item.StreetAddress1}` : ""}${item.City ? `, ${item.City}` : ""}
												${item.State ? `, ${item.State}` : ""}${item.Zipcode ? `, ${item.Zipcode}` : ""}`}
										</small>
										<small>
											<div>
												<strong>{`${lang.t("Coordinates")}:`}</strong>
											</div>
											{` ${item.Latitude.toFixed(4)}, ${item.Longitude.toFixed(4)}`}
										</small>
									</div>

									<div className="col-sm-4 col-xs-6">
										<div hidden={!item.ClientSupervisorContactId}>
											<span className="visible-xs-inline-block client-view-contact-detail">
												<strong>{`${lang.t("Contact Details")}:`}</strong>
												&nbsp;
											</span>
											<div>{item.ClientSupervisorName}</div>
											<a
												className="btn btn-link"
												href={`mailto:${item.ClientSupervisorEmail}`}
												hidden={!item.ClientSupervisorEmail}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="rkf rkf-email" />
												{lang.t("EMail")}
											</a>
											<br />
											<a
												className="btn btn-link"
												href={`tel:${item.ClientSupervisorMobile}`}
												hidden={!item.ClientSupervisorMobile}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="rkf rkf-phone" />
												{utils.formatPhoneNumber(
													item.ClientSupervisorMobile,
												)}
											</a>
										</div>
									</div>

									<div className="col-sm-2 col-xs-6 text-right">
										<button
											type="button"
											className="btn btn-link client-view-round-button btn-edit"
											onClick={() =>
												this.setState({ editingLocation: item.Id })
											}
											title={lang.t("Edit Contact")}
										>
											<i className="rkf rkf-pencil" />
										</button>
										<button
											type="button"
											className="btn btn-link client-view-round-button btn-delete"
											onClick={this.onDeleteLocation(item)}
											title={lang.t("Delete Contact")}
										>
											<i className="rkf rkf-trash" />
										</button>
									</div>
								</div>
							</For>
							<If condition={!locationsLoaded}>
								<ScrollDetect
									className="text-center"
									onVisible={this.getLocations}
								>
									<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
								</ScrollDetect>
							</If>
						</div>
					</div>
				</div>
				<LocationModal
					show={addingLocation || editingLocation !== undefined}
					onHide={this.onHideLocationModal}
					onSuccess={() => {
						this.onHideLocationModal();
						this.getLocations();
					}}
					title={
						addingLocation
							? lang.t("Add Client Location")
							: lang.t("Edit Client Location")
					}
					contacts={this.parseClientContacts()}
					locationId={editingLocation}
					projectId={projectId}
					user={user}
				/>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
			</div>
		);
	}
}

LocationsTab.propTypes = {
	clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	contacts: PropTypes.array,
	user: PropTypes.object,
};

LocationsTab.defaultProps = {
	clientId: "",
	contacts: [],
	user: {},
};

export default LocationsTab;
